import { Injectable } from '@angular/core';
import { from, Observable, ReplaySubject } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { DateFnsConfigurationService } from 'ngx-date-fns';
import { dateFnsLocales, LocaleDefinition } from './date-fns-locales';
import { Locale } from 'date-fns';

export const currentLocale$ = new ReplaySubject<Locale | null>(1);

/**
 * Return current browser's languages ordered by user's preference. Example: [`ar`, `pl-PL`]
 * It may not equal userLanguage set by user!
 */
const browserLanguages = (): readonly string[] => {
  if (navigator.languages) { return navigator.languages; }

  // navigator.languages is still experimental, if not set we need to fallback
  return navigator.language ? [navigator.language] : [];
};

const shortISOLanguageCode = (code: string): string => {
  return code.split('-')[0];
};

@Injectable({
  providedIn: 'root',
})
export class DateFnsLocaleService {

  constructor(private config: DateFnsConfigurationService) {}

  public init(): void {
    const localeFn = this.dateFnsLocaleImport();
    if (!localeFn) { return currentLocale$.next(null); }

    localeFn()?.pipe(first()).subscribe((locale) => {
      currentLocale$.next(locale);
      (window as any).currentDateFnsLocale = locale;
      this.config.setLocale(locale);
    });
  }

  private dateFnsLocaleImport(): (() => Observable<Locale> | null) | null {
    for (const language of browserLanguages()) {
      let localeKey = language;
      let localeDef = this.fetchLocale(localeKey);

      if (!localeDef) {
        localeKey = shortISOLanguageCode(localeKey);
        localeDef = this.fetchLocale(localeKey);
      }

      if (localeDef) {
        const importKey = localeDef.key || localeKey;

        return () => from(localeDef.fn()).pipe(map(localeFn => localeFn[importKey]));
      }
    }

    return null;
  }

  private fetchLocale(key: string): LocaleDefinition {
    return dateFnsLocales[key];
  }
}
